import * as Actions from '../actions/questionActions';
import Question from '../../entities/question';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type QuestionAction = { 
    type: string; 
    question: Question | null; 
    questionId: string | Question; 
    partial: boolean; 
    lastFetch: number; 
    questions: Question[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Question> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Question[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Question> | null, firmId: string, userId: string, updates?: Question[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'question', 'questions', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Question>} data
 * @param {Question[]} [updates]
 * @returns {ArrayMap<Question>}
 */
function updateStorage(data: ArrayMap<Question>, updates?: Question[]): ArrayMap<Question> {
	if (logging) console.debug('⮂ questionReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Question[]} [updates]
 * @returns {ArrayMap<Question>}
 */
function updateStorageNoReturn(updates?: Question[]): void {
	if (logging) console.debug('⮂ questionReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Question[]} [data]
 * @returns {ArrayMap<Question>}
 */
function createState(data?: Question[] | ArrayMap<Question>): ArrayMap<Question> {
	if (!data) return ArrayMapRegistry.clone<Question>('question', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Question>('question', data.map(e => e));
    else return ArrayMapRegistry.clone<Question>('question', data);
}

function questions(state: ArrayMap<Question>, action: QuestionAction): ArrayMap<Question> {
	if (!state) state = createState();

	let st: ArrayMap<Question>;

	switch (action.type) {
		// action.question - the new value of question with id action.id
		// action.questionId - id of the target, used if new value is null
		case Actions.QUESTION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ questionReducer: FETCH_ONE_SUCCESS', action);

			if (action.question === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.questionId)) {
				st = state.clone();
				st.set(action.question);
				// state.clear();

				updateStorageNoReturn([action.question]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.questions
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.questions
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.questions
			 !partial, !stateEmpty, fetchEmpty	-> D return questions in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge questions in state newer than those in action.questions
		 */
		case Actions.QUESTION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.questions.length === 0;
			const fetched = action.questions.map(question => new Question({...question, persisted: true}));

			if (logging) console.debug(`⮂ questionReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} questions with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ questionReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ questionReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Question> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Question[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(question => {
					const current = st.get(question.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && question.status === 'D') {
							// Remove it from state
							toUpdate.push(question);
							st.delete(question);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (question.addedOn > current.addedOn) {
								toUpdate.push(question);
								st.set(question);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (question.status !== 'D') {
							toUpdate.push(question);
							st.set(question);
						}
						else if (logging) console.debug('⮂ questionReducer: partialMerge: skipping deleted question not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ questionReducer: empty: questions', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ questionReducer: fullMerge: questions', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localQuestion => {
                    // If this question has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localQuestion.persisted) {
                        const fetchedQuestion = st.get(localQuestion.id);
                        if (!fetchedQuestion || localQuestion.addedOn > fetchedQuestion.addedOn) st.set(localQuestion);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.QUESTION_FETCH_ONE_FAILURE:
			console.error('QUESTION_FETCH_ONE_FAILURE', action.questionId, action.err);

			// No update to storage required
			return state;

		case Actions.QUESTION_FETCH_FAILURE:
			console.error('QUESTION_FETCH_FAILURE', action.questionId, action.err);

			// No update to storage required
			return state;

		case Actions.QUESTION_RESTORE: {
		    let question = new Question({...action.question});
            st = state.clone();

            const existing = st.get(question.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (question.addedOn >= existing.addedOn) {
                    st.set(question);
                    return updateStorage(st, [question]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (question.status === 'D') return state;

                st.set(question);
                return updateStorage(st, [question]);
            }
        }
            
		case Actions.QUESTION_SAVE: {
		    // console.debug('⮂ QUESTION_SAVE reducer', action.question?.id);
		    
			// clone the question
			let question = new Question({...action.question});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new question in the state
			// If this is a create, add to the queue
			const _question = st.get(question.id);

			if (!_question) st.set(question);
			else {
				if (question.addedOn >= _question.addedOn) st.set(question);
			}

			return updateStorage(st, [question]);
		}

		case Actions.QUESTION_SAVE_MANY: {
		    if (logging) console.debug('⮂ QUESTION_SAVE_MANY reducer', action.questions.map(question => question.id).join(','));
		    
			// clone the questions
			let toProcess = action.questions.map(question => new Question(question));

			st = state.clone();
			// state.clear();

			const updates: Question[] = [];

			toProcess.forEach(question => {
				// Add or update the new question in the state
				// If this is a create, add to the queue
				const existing = st.get(question.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (question.status !== 'D') {
						st.push(question);
						updates.push(question);
					}
					else if (logging) console.debug('⮂ QUESTION_SAVE_MANY: skipping deleted question not in state');
				}
				else {
					if (question.addedOn >= existing.addedOn) {
						st.set(question);
						updates.push(question);
					}
					else if (logging) console.debug('⮂ Skipping, existing question had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.QUESTION_REMOVE: {
			// clone the question
			const question = new Question({...action.question});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(question);

			return updateStorage(st, [question]);
		}

		case Actions.QUESTION_RESTORE_FAILURE:
		case Actions.QUESTION_SAVE_FAILURE: {
		    console.warn('QUESTION_SAVE_FAILURE reducer', action.question?.id);
		    if (!action.question?.id) {
				console.warn('QUESTION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const question = ensure(Question, action.question);
			if (!question) {
				console.error('Could not find question after call to save');
				return state;
			}

			st = state.clone();

			const _questionRef = state.get(question.id);

			if (_questionRef && (_questionRef.addedOn === question.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _question = new Question({..._questionRef});
				_question.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_question.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('question', _question).then(r => {
						if (r.isErr()) console.error(`⮂ questionReducer: failed to save question with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ questionReducer: saved question with fatalError info');
					}).catch(err => console.error(`⮂ questionReducer: failed to save question with fatalError info: ${err.message}`));

				st.set(_question);
			}

			return updateStorage(st);
		}

		case Actions.QUESTION_SAVE_MANY_FAILURE: {
		    console.warn('QUESTION_SAVE_MANY_FAILURE reducer', action.questions.map(question => question.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.QUESTION_RESTORE_SUCCESS:
		case Actions.QUESTION_SAVE_SUCCESS: {
			if (!action.question?.id) {
				console.warn('QUESTION_SAVE_SUCCESS reducer');
                return state;
			}

			const questionRef = state.get(action.question?.id);
			if (!questionRef) {
				console.warn('Could not find question after call to save');
                return state;
			}

			st = state.clone();

			if (action.question.addedOn >= questionRef.addedOn) {
				st.set(action.question);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const question = new Question({...questionRef, persisted: true});
				st.set(question);
			}

			return updateStorage(st, [action.question]);
		}

		case Actions.QUESTION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ QUESTION_SAVE_MANY_SUCCESS reducer', action.questions.map(question => question.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const questions: Question[] = [];

			action.questions.forEach(e => {
				const questionRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!questionRef) console.warn('Could not find question after call to save');
				else if (e.addedOn >= questionRef.addedOn) {
				    st.set(e);
				    questions.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const question = new Question({...questionRef, persisted: true});
					st.set(question);
					questions.push(question);
				}
			});

			return updateStorage(st, questions);
		}

		case Actions.QUESTION_REMOVE_FAILURE: {
		    console.warn('QUESTION_REMOVE_FAILURE action', action.question?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.QUESTION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.QUESTION_RESTORE_MANY: {
			if (logging) console.debug('⮂ QUESTION_RESTORE_MANY reducer', action.questions.map(question => question.id).join(','));

			const toProcess = action.questions.map(question => new Question({...question}));
			st = state.clone();

			const updates: Question[] = [];

			toProcess.forEach(question => {
				const existing = st.get(question.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (question.addedOn >= existing.addedOn) {
						st.set(question);
						updates.push(question);
					}
					else if (logging) console.debug('⮂ QUESTION_RESTORE_MANY: skipping older version of question');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (question.status !== 'D') {
						st.set(question);
						updates.push(question);
					}
					else if (logging) console.debug('⮂ QUESTION_RESTORE_MANY: skipping deleted question not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.QUESTION_REMOVE_MANY: {
			if (logging) console.debug('⮂ QUESTION_REMOVE_MANY reducer', action.questions.map(question => question.id).join(','));

			const toProcess = action.questions.map(question => new Question({...question}));
			st = state.clone();

			toProcess.forEach(question => {
				st.delete(question);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default questions;